import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EndpointService } from '../endpoints.service';
import { StorageService } from '../../@core/services/storage.service';
import {
  StripeConnectStatus,
  OrderResponse,
  OrderRefundResponse
} from '../models/response.module';

export interface CreateCheckoutParams {
  event_id: number;
  class_registrations: Array<{
    event_class_id: number;
    user_id: string;
    horse_id: number;
  }>;
  service_registrations: Array<{
    event_service_id: number;
    user_id: string;
    num_registrations: number;
  }>;
}

export interface PayLaterParams {
  event_id: number;
  class_registrations: Array<{
    event_class_id: number;
    user_id: string;
    horse_id: number;
  }>;
  service_registrations: Array<{
    event_service_id: number;
    user_id: string;
    num_registrations: number;
  }>;
}

export interface TrainingCheckoutParams {
  event_id: number;
  package_registrations: Array<{
    package_id: number;
    user_id: string;
    horse_id?: number;
  }>;
  service_registrations: Array<{
    event_service_id: number;
    user_id: string;
    num_registrations: number;
  }>;
}

export interface TrainingPayLaterParams {
  event_id: number;
  package_registrations: Array<{
    package_id: number;
    user_id: string;
    horse_id?: number;
  }>;
  service_registrations: Array<{
    event_service_id: number;
    user_id: string;
    num_registrations: number;
  }>;
}

export interface RefundParams {
  amount_cents?: number;  // Optional - null/0 for full refund
  reason?: string;        // Optional - refund reason
}

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  constructor(
    private endpointService: EndpointService,
    private storageService: StorageService
  ) {}

  /**
   * Create Stripe Connect account and start onboarding
   * @param company_id - The company ID to create account for
   * @param business_type - 'individual' or 'company' (default: 'company')
   * @param accept_fee_terms - Whether the user has accepted the platform fee terms
   */
  createConnectAccount(company_id: string, business_type: 'individual' | 'company' = 'company', accept_fee_terms: boolean = false): Observable<{ onboarding_url: string; account_id: string }> {
    return this.endpointService.aqh_pay_1({ company_id, business_type, accept_fee_terms }).pipe(
      map(response => response.data)
    );
  }

  /**
   * Get company's Stripe Connect account status
   */
  getConnectStatus(company_id: string): Observable<StripeConnectStatus> {
    return this.endpointService.aqh_pay_2({ company_id }).pipe(
      map(response => response.data)
    );
  }

  /**
   * Refresh onboarding link for incomplete setup
   */
  refreshOnboarding(company_id: string): Observable<{ onboarding_url: string }> {
    return this.endpointService.aqh_pay_3({ company_id }).pipe(
      map(response => response.data)
    );
  }

  /**
   * Get Stripe Express dashboard login link
   */
  getDashboardLink(company_id: string): Observable<{ dashboard_url: string }> {
    return this.endpointService.aqh_pay_4({ company_id }).pipe(
      map(response => response.data)
    );
  }

  /**
   * Create Stripe Checkout session for event registration
   */
  createCheckoutSession(params: CreateCheckoutParams): Observable<{ checkout_url: string; session_id: string; order_id: number; free_order?: boolean }> {
    return this.endpointService.aqh_pay_5(params).pipe(
      map(response => response.data)
    );
  }

  /**
   * Get orders for user or event
   */
  getOrders(params: { user_id?: string; event_id?: number }): Observable<OrderResponse[]> {
    return this.endpointService.aqh_pay_6(params).pipe(
      map(response => response.data)
    );
  }

  /**
   * Get specific order details
   */
  getOrder(order_id: number): Observable<OrderResponse> {
    return this.endpointService.aqh_pay_7(order_id).pipe(
      map(response => response.data)
    );
  }

  /**
   * Create registration with pay-later status
   */
  createPayLaterRegistration(params: PayLaterParams): Observable<{ order_id: number; status: string; message: string }> {
    return this.endpointService.aqh_pay_12(params).pipe(
      map(response => response.data)
    );
  }

  /**
   * Pay a pending order - creates Stripe Checkout session for existing order
   */
  payPendingOrder(order_id: number): Observable<{ checkout_url: string; session_id: string; order_id: number }> {
    return this.endpointService.aqh_pay_10({ order_id }).pipe(
      map(response => response.data)
    );
  }

  /**
   * Cancel a pending pay-later order
   */
  cancelOrder(order_id: number): Observable<{ order_id: number; status: string; message: string }> {
    return this.endpointService.aqh_pay_11({ order_id }).pipe(
      map(response => response.data)
    );
  }

  /**
   * Create Stripe Checkout session for membership purchase
   */
  createMembershipCheckout(plan_id: number, success_url?: string, cancel_url?: string): Observable<{ checkout_url: string; session_id: string; order_id: number; membership_id: number }> {
    return this.endpointService.aqh_pay_13({ plan_id, success_url, cancel_url }).pipe(
      map(response => response.data)
    );
  }

  /**
   * Create Stripe Checkout session for training event registration
   */
  createTrainingCheckoutSession(params: TrainingCheckoutParams): Observable<{ checkout_url: string; session_id: string; order_id: number; free_order?: boolean }> {
    return this.endpointService.aqh_pay_14(params).pipe(
      map(response => response.data)
    );
  }

  /**
   * Create training registration with pay-later status
   */
  createTrainingPayLaterRegistration(params: TrainingPayLaterParams): Observable<{ order_id: number; status: string; message: string }> {
    return this.endpointService.aqh_pay_15(params).pipe(
      map(response => response.data)
    );
  }

  /**
   * Process full or partial refund for an order
   * @param order_id - The order ID to refund
   * @param params - Optional params: amount_cents (for partial), reason
   */
  refundOrder(order_id: number, params: RefundParams = {}): Observable<OrderRefundResponse> {
    return this.endpointService.aqh_pay_20(order_id, params).pipe(
      map(response => response.data)
    );
  }

  /**
   * Create checkout session for store/service company registration
   * Registration fee: 299 NOK - payment goes to platform
   */
  createStoreCheckout(params: {
    company_name: string;
    company_type: string;
    category_id: number;
    organisation_number?: string;
    address?: string;
    city?: string;
    postal_code?: string;
    country?: string;
    country_id?: number;
    phone?: string;
    email?: string;
    description?: string;
  }): Observable<{ checkout_url: string; session_id: string; pending_registration_id: number }> {
    return this.endpointService.aqh_pay_21(params).pipe(
      map(response => response.data)
    );
  }

  /**
   * Create checkout session for riding club pro subscription
   * Unlocks membership plans and competition events
   * Subscription: 299 NOK/year - payment goes to platform
   */
  createClubProCheckout(company_id: string, success_url?: string, cancel_url?: string): Observable<{ checkout_url: string; session_id: string }> {
    return this.endpointService.aqh_pay_22({ company_id, success_url, cancel_url }).pipe(
      map(response => response.data)
    );
  }
}
